import { Dispatch } from 'react';
import { appInsights } from '../AppInsights';
import { ClientConfiguration, IGptConfiguration, ISharedGptConfiguration } from '../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { EnterpriseGPTApiV1ClientWrapper } from '../EnterpriseGPTApi/EnterpriseGPTApiV1ClientWrapper';
import { setGptConfigurationsAction } from '../store/actions/gptConfigurationsAction';
import { RootStateAction } from '../store/actions/rootStateActions';
import { UserProfile } from '../Contexts/UserContext';
import { GptConfigurationEx } from '../store/models/GptConfigurationEx';
import { getUserInfoDebug } from './personaUtils';

/**
 * Loads GPT configurations from the server with attachment configuration fallback
 * @param client - The API client instance
 * @param clientConfiguration - The client configuration (can be null if not yet loaded)
 * @param userProfile - The user profile for logging
 * @param abortSignal - Optional abort signal for cancellation
 * @returns Array of GPT configurations with attachment fallback applied
 */
export async function loadGptConfigurationsWithFallback(
    client: EnterpriseGPTApiV1ClientWrapper,
    clientConfiguration: ClientConfiguration | null,
    userProfile: UserProfile | null,
    abortSignal?: AbortSignal
): Promise<IGptConfiguration[]> {
    console.log('Loading gpt Configurations with fallback...');
    const gptConfigurations = await client.getGptConfigurations(true, abortSignal);
    console.log('gptConfigurations:', gptConfigurations);
    appInsights.trackTrace({ 
        message: 'gptConfigurations loaded; ' + getUserInfoDebug(userProfile ?? undefined),
        properties: gptConfigurations });

    // Apply attachment configuration fallback for personal GPTs
    const gptConfigurationsWithFallback = gptConfigurations.map(gpt => {

        // If it's a personal GPT without its own attachment configuration, 
        // assign the global personal GPT attachment configuration
        if ((gpt.isPersonal) && clientConfiguration?.personalGpt?.attachmentConfiguration) {
            // Use the personal GPT attachment configuration from client configuration
            // This will ensure that personal GPTs have the correct attachment settings
            const personalAttachmentConfig = clientConfiguration.personalGpt.attachmentConfiguration;
            
            // Check if personal GPT attachment configuration extensions are empty or null
            if (!personalAttachmentConfig.extensions || personalAttachmentConfig.extensions.length === 0) {
                // Find the default GPT to get its extensions
                const defaultGpt = gptConfigurations.find(g => g.isDefault);
                
                if (defaultGpt?.attachment?.extensions && defaultGpt.attachment.extensions.length > 0) {
                    // Use default GPT's extensions with personal GPT's other attachment settings
                    return {
                        ...gpt,
                        attachment: {
                            ...personalAttachmentConfig,
                            extensions: defaultGpt.attachment.extensions
                        }
                    };
                }
            }
            
            return {
                ...gpt,
                attachment: personalAttachmentConfig
            };
        }
        return gpt;
    });
    
    console.log('gptConfigurationsWithFallback:', gptConfigurationsWithFallback);
    appInsights.trackTrace({ 
        message: 'gptConfigurationsWithFallback; ' + getUserInfoDebug(userProfile ?? undefined),
        properties: gptConfigurationsWithFallback });

    return gptConfigurationsWithFallback;
}

/**
 * Loads shared GPT configurations from the server with attachment configuration fallback
 * @param client - The API client instance
 * @param clientConfiguration - The client configuration (can be null if not yet loaded)
 * @param userProfile - The user profile for logging
 * @param abortSignal - Optional abort signal for cancellation
 * @returns Array of shared GPT configurations with attachment fallback applied
 */
export async function loadSharedGptsWithFallback(
    gptConfigurations: IGptConfiguration[],
    client: EnterpriseGPTApiV1ClientWrapper,
    clientConfiguration: ClientConfiguration | null,
    userProfile: UserProfile | null,
    abortSignal?: AbortSignal
): Promise<ISharedGptConfiguration[]> {
    console.log('Loading shared GPTs with fallback...');
    const sharedGpts = await client.getSharedGpts(false, abortSignal);
    console.log('sharedGpts:', sharedGpts);
    appInsights.trackTrace({ 
        message: 'sharedGpts loaded; ' + getUserInfoDebug(userProfile ?? undefined),
        properties: sharedGpts });

    // Apply attachment configuration fallback for personal GPTs
    const sharedGptsWithFallback = sharedGpts.map(gpt => {

        // If it's a personal GPT without its own attachment configuration, 
        // assign the global personal GPT attachment configuration
        if ((gpt.isPersonal) && clientConfiguration?.personalGpt?.attachmentConfiguration) {
            // Use the personal GPT attachment configuration from client configuration
            // This will ensure that personal GPTs have the correct attachment settings
            const personalAttachmentConfig = clientConfiguration.personalGpt.attachmentConfiguration;
            
            // Check if personal GPT attachment configuration extensions are empty or null
            if (!personalAttachmentConfig.extensions || personalAttachmentConfig.extensions.length === 0) {
                // Find the default GPT to get its extensions
                const defaultGpt = gptConfigurations.find(g => g.isDefault);
                
                if (defaultGpt?.attachment?.extensions && defaultGpt.attachment.extensions.length > 0) {
                    // Use default GPT's extensions with personal GPT's other attachment settings
                    return {
                        ...gpt,
                        attachment: {
                            ...personalAttachmentConfig,
                            extensions: defaultGpt.attachment.extensions
                        }
                    };
                }
            }
            
            return {
                ...gpt,
                attachment: personalAttachmentConfig
            };
        }
        return gpt;
    });
    
    console.log('sharedGptsWithFallback:', sharedGptsWithFallback);
    appInsights.trackTrace({ 
        message: 'sharedGptsWithFallback; ' + getUserInfoDebug(userProfile ?? undefined),
        properties: sharedGptsWithFallback });

    return sharedGptsWithFallback;
}


/**
 * Loads GPT configurations without chat counts (for sidebar and initial app load)
 * @param client - The API client instance
 * @param dispatch - The store dispatch function
 * @param clientConfiguration - The client configuration (can be null if not yet loaded)
 * @param userProfile - The user profile for logging
 * @param abortSignal - Optional abort signal for cancellation
 */
export async function loadGptConfigurationsWithoutCounts(
    client: EnterpriseGPTApiV1ClientWrapper,
    dispatch: Dispatch<RootStateAction>,
    clientConfiguration: ClientConfiguration | null,
    userProfile: UserProfile | null,
    abortSignal?: AbortSignal
): Promise<void> {
    // Load personal/default GPTs and shared GPTs sequentially
    const gptConfigurations = await loadGptConfigurationsWithFallback(client, clientConfiguration, userProfile, abortSignal);
    const sharedGpts = await loadSharedGptsWithFallback(gptConfigurations, client, clientConfiguration, userProfile, abortSignal);

    // Convert personal/default GPTs to GptConfigurationEx[] with isShared: false
    const personalGptConfigurationsEx: GptConfigurationEx[] = gptConfigurations.map(gpt => {
        const gptEx = new GptConfigurationEx(gpt);
        gptEx.isShared = false;
        return gptEx;
    });

    // Convert shared GPTs to GptConfigurationEx[] with isShared: true
    const sharedGptConfigurationsEx: GptConfigurationEx[] = sharedGpts.map(gpt => {
        const gptEx = new GptConfigurationEx(gpt);
        gptEx.isShared = true;
        gptEx.ownerEmail = gpt.ownerEmail;
        gptEx.ownerFullName = gpt.ownerFullName;
        return gptEx;
    });

    // Merge both arrays
    const allGptConfigurationsEx = [...personalGptConfigurationsEx, ...sharedGptConfigurationsEx];

    console.log('allGptConfigurationsEx (without counts):', allGptConfigurationsEx);
    appInsights.trackTrace({ 
        message: 'allGptConfigurationsEx loaded (without counts); ' + getUserInfoDebug(userProfile ?? undefined),
        properties: allGptConfigurationsEx });

    dispatch(setGptConfigurationsAction(allGptConfigurationsEx));
}

/**
 * Loads GPT configurations with chat counts (for Explore GPTs page)
 * @param client - The API client instance
 * @param dispatch - The store dispatch function
 * @param clientConfiguration - The client configuration (can be null if not yet loaded)
 * @param userProfile - The user profile for logging
 * @param abortSignal - Optional abort signal for cancellation
 */
export async function loadGptConfigurations(
    client: EnterpriseGPTApiV1ClientWrapper,
    dispatch: Dispatch<RootStateAction>,
    clientConfiguration: ClientConfiguration | null,
    userProfile: UserProfile | null,
    abortSignal?: AbortSignal
): Promise<void> {
    // Load personal/default GPTs and shared GPTs sequentially
    const gptConfigurations = await loadGptConfigurationsWithFallback(client, clientConfiguration, userProfile, abortSignal);
    const sharedGpts = await loadSharedGptsWithFallback(gptConfigurations, client, clientConfiguration, userProfile, abortSignal);

    // Convert personal/default GPTs to GptConfigurationEx[] with isShared: false
    const personalGptConfigurationsEx: GptConfigurationEx[] = gptConfigurations.map(gpt => {
        const gptEx = new GptConfigurationEx(gpt);
        gptEx.isShared = false;
        return gptEx;
    });

    // Convert shared GPTs to GptConfigurationEx[] with isShared: true
    const sharedGptConfigurationsEx: GptConfigurationEx[] = sharedGpts.map(gpt => {
        const gptEx = new GptConfigurationEx(gpt);
        gptEx.isShared = true;
        gptEx.ownerEmail = gpt.ownerEmail;
        gptEx.ownerFullName = gpt.ownerFullName;
        return gptEx;
    });

    // Merge both arrays
    const allGptConfigurationsEx = [...personalGptConfigurationsEx, ...sharedGptConfigurationsEx];

    // Initialize numberOfChats to 0 for all GPTs
    allGptConfigurationsEx.forEach(gpt => {
        gpt.numberOfChats = 0;
    });

    // Get the number of chats by GPT
    let numberOfChatsByGpt: { [key: string]: number } | null = null;
    try {
        numberOfChatsByGpt = await client.getNumberOfChatsByGPTForUser(abortSignal);
        // Log the raw response so we can inspect the structure in the console
        console.log('numberOfChatsByGpt response:', numberOfChatsByGpt);
        appInsights.trackTrace({ message: 'numberOfChatsByGpt response', properties: numberOfChatsByGpt ?? {} });
    } catch (err) {
        console.error('Error fetching numberOfChatsByGpt:', err);
        appInsights.trackException({ exception: err as Error });
    }


    // Update numberOfChats property for each GPT configuration
    // The API returns a dictionary where key is the GPT ID (Guid) and value is the numberOfChats (int)
    if (numberOfChatsByGpt && typeof numberOfChatsByGpt === 'object') {
        Object.entries(numberOfChatsByGpt).forEach(([gptId, chatCount]) => {
            const gpt = allGptConfigurationsEx.find(g => g.id === gptId);
            if (gpt) {
                gpt.numberOfChats = chatCount;
            }
        });
    }

    console.log('allGptConfigurationsEx (with counts):', allGptConfigurationsEx);
    appInsights.trackTrace({ 
        message: 'allGptConfigurationsEx created (with counts); ' + getUserInfoDebug(userProfile ?? undefined),
        properties: allGptConfigurationsEx });

    dispatch(setGptConfigurationsAction(allGptConfigurationsEx));
}
